import {
  Container,
  Header,
  Table,
  StatusIndicator,
  Box,
  Button,
  SpaceBetween,
  TextContent,
  TableProps,
  Alert,
} from '@cloudscape-design/components';
import React, { useState, useMemo, useEffect } from 'react';
import {
  AuditStatus,
  DEFAULT_LANGUAGE,
  QUESTION_TYPE_LABELS,
  QuestionResultType,
  ScoreFilterType,
} from 'src/contstant/commonLabels';
import {
  Audit,
  QuestionWithResponse,
  SubQuestionResponseEntry,
} from 'src/types/helios/Audit';
import { getAuditQuestionResponseStatusIcon } from 'src/utils/generalUtils';
import { Task } from 'src/types/helios/Task';
import { TaskStatus } from 'src/contstant/taskStatusColors';
import { LanguageDropdown } from '../LanguageDropdown';
import useUserProfile from 'src/hooks/useUserProfile';
import { getQuestionContent } from 'src/helper/questionHelper';
import { convertMediaResponseToMediaData } from 'src/utils/mediaUtils';
import AuditSummaryTaskPanel from './AuditSummaryTaskPanel';
import SafeHtml from '../SafeHtml';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import { useNavigate } from 'react-router-dom';
import { openTaskDetailsPage } from 'src/helper/taskHelper';

interface QuestionSummaryTableProps {
  questions: QuestionWithResponse[];
  isLoading: boolean;
  audit: Audit;
  tasks?: Task[];
  selectedFilter?: string | null;
  showLanguageDropdown?: boolean;
  paginationControls?: React.ReactNode;
  /** Called after a task is successfully created — parent should reload tasks */
  onTaskCreated?: () => void;
}

const QuestionSummaryTable = ({
  audit,
  questions,
  isLoading,
  tasks,
  selectedFilter,
  showLanguageDropdown = true,
  paginationControls,
  onTaskCreated,
}: QuestionSummaryTableProps) => {
  const [isTaskPanelOpen, setIsTaskPanelOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<
    QuestionWithResponse | undefined
  >();
  const [selectedSubQuestionId, setSelectedSubQuestionId] = useState<
    string | undefined
  >();
  const [selectedExistingTask, setSelectedExistingTask] = useState<Task | undefined>();
  const [isSelectedTaskReadOnly, setIsSelectedTaskReadOnly] = useState(false);
  const [isRefreshingTasks, setIsRefreshingTasks] = useState(false);
  const { getUserSelectedLanguage, getPreferLanguageList, selectUserLanguage } =
    useUserProfile();
  const [translationError] = useState('');
  const { isOpen: isSplitPanelOpen } = useSplitPanel();
  const navigate = useNavigate();

  // Clear refreshing state when tasks list updates after creation
  useEffect(() => {
    if (isRefreshingTasks) {
      setIsRefreshingTasks(false);
    }
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort questions to show failed questions first only when no filter is applied
  const sortedQuestions = useMemo(() => {
    // If a specific filter is applied, don't sort (maintain original order)
    if (selectedFilter && selectedFilter !== ScoreFilterType.TOTAL) {
      return questions;
    }

    // Sort to show failed questions first when showing all
    return [...questions].sort((firstQuestion, secondQuestion) => {
      const firstQuestionHasFailed =
        firstQuestion.responseDetails?.mainQuestionResponse?.responseResult ===
          QuestionResultType.FAIL ||
        firstQuestion.responseDetails?.subQuestionResponses?.some(
          (sub) => sub.response.responseResult === QuestionResultType.FAIL,
        );
      const secondQuestionHasFailed =
        secondQuestion.responseDetails?.mainQuestionResponse?.responseResult ===
          QuestionResultType.FAIL ||
        secondQuestion.responseDetails?.subQuestionResponses?.some(
          (sub) => sub.response.responseResult === QuestionResultType.FAIL,
        );
      return secondQuestionHasFailed
        ? firstQuestionHasFailed
          ? 0
          : 1
        : firstQuestionHasFailed
          ? -1
          : 0;
    });
  }, [questions, selectedFilter]);

  const handleGenerateTask = (question: QuestionWithResponse, subQuestionId?: string) => {
    const task = hasTask(question.questionDetails.questionId, subQuestionId);

    // Completed tasks → navigate to task details page instead of opening the panel
    if (task && task.taskStatus?.toUpperCase() === TaskStatus.COMPLETED) {
      openTaskDetailsPage(navigate, task);
      return;
    }

    setSelectedQuestion(question);
    setSelectedSubQuestionId(subQuestionId);
    setSelectedExistingTask(task);
    setIsSelectedTaskReadOnly(isTaskNonEditable(task));
    setIsTaskPanelOpen(true);
  };

  const hasTask = (questionId?: string, subQuestionId?: string): Task | undefined =>
    tasks?.find(
      (item) => item.questionId === questionId && item.subQuestionId === subQuestionId,
    );

  /** Returns true when the task is in a terminal state that should not be editable */
  const isTaskNonEditable = (task?: Task): boolean => {
    const status = task?.taskStatus?.toUpperCase();
    return status === TaskStatus.CANCELLED || status === TaskStatus.COMPLETED;
  };

  const columnDefinitions: TableProps.ColumnDefinition<SubQuestionResponseEntry>[] = [
    {
      id: 'subQuestionId',
      header: 'Sub-Question ID',

      cell: (item: SubQuestionResponseEntry) => item.subQuestionId,
    },
    {
      id: 'subQuestionText',
      header: 'Sub-Question',
      minWidth: 180,
      maxWidth: window.innerWidth / 6,
      cell: (item: SubQuestionResponseEntry) => {
        return (
          <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
            {item.subQuestion?.title}
          </div>
        );
      },
    },
    ...(audit.status === AuditStatus.COMPLETED
      ? [
          {
            id: 'subComment',
            header: 'Audit Comments',
            minWidth: 180,
            maxWidth: window.innerWidth / 6,
            cell: (item: SubQuestionResponseEntry) => (
              <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                {item.response.responseComment || '-'}
              </div>
            ),
          },
        ]
      : []),
    ...(audit.status === AuditStatus.COMPLETED
      ? [
          {
            id: 'responseResult',
            header: 'Result',
            cell: (item: SubQuestionResponseEntry) => {
              return (
                <StatusIndicator
                  data-testid="sub-question-status-indicator"
                  type={getAuditQuestionResponseStatusIcon(item.response.responseResult)}
                >
                  {' '}
                  {
                    QUESTION_TYPE_LABELS[
                      item.response.responseResult as keyof typeof QuestionResultType
                    ]
                  }
                </StatusIndicator>
              );
            },
          },
        ]
      : []),
    ...(audit.status === AuditStatus.COMPLETED
      ? [
          {
            id: 'actions',
            header: 'Actions',
            minWidth: 200,
            cell: (item: SubQuestionResponseEntry & { parentQuestionId: string }) =>
              item.response.responseResult === QuestionResultType.FAIL ? (
                <Button
                  data-testid="task-button"
                  loading={isRefreshingTasks}
                  variant={
                    isSplitPanelOpen &&
                    selectedQuestion?.questionDetails.questionId ===
                      item.parentQuestionId &&
                    selectedSubQuestionId === item.subQuestionId
                      ? 'primary'
                      : 'normal'
                  }
                  onClick={() =>
                    handleGenerateTask(
                      sortedQuestions.find(
                        (q) => q.questionDetails.questionId === item.parentQuestionId,
                      )!,
                      item.subQuestionId,
                    )
                  }
                >
                  {!hasTask(item.parentQuestionId, item.subQuestionId)
                    ? 'Generate Task'
                    : 'View Task'}
                </Button>
              ) : (
                '-'
              ),
          },
        ]
      : []),
  ];

  function renderQuestionRow(item: QuestionWithResponse): any {
    const questionDetails = getQuestionContent(
      item.questionDetails,
      getUserSelectedLanguage(),
    );
    return (
      <Container
        data-testid="question-container"
        key={item.questionDetails.questionId}
        footer={
          item?.responseDetails?.mainQuestionResponse.responseResult ===
            QuestionResultType.FAIL &&
          audit.status === AuditStatus.COMPLETED && (
            <Button
              data-testid="footer-task-button"
              onClick={() => handleGenerateTask(item)}
              loading={isRefreshingTasks}
              variant={
                isSplitPanelOpen &&
                selectedQuestion?.questionDetails.questionId ===
                  item.questionDetails.questionId &&
                !selectedSubQuestionId
                  ? 'primary'
                  : 'normal'
              }
            >
              {!hasTask(item.questionDetails.questionId) ? 'Generate Task' : 'View Task'}
            </Button>
          )
        }
      >
        {/* Question Section */}
        <SpaceBetween data-testid="question-content" size="m">
          <SpaceBetween
            data-testid="question-header"
            size="m"
            direction="horizontal"
            alignItems="center"
          >
            {audit.status === AuditStatus.COMPLETED && (
              <StatusIndicator
                data-testid="main-question-status-indicator"
                type={getAuditQuestionResponseStatusIcon(
                  item?.responseDetails?.mainQuestionResponse.responseResult,
                )}
              >
                {
                  QUESTION_TYPE_LABELS[
                    item?.responseDetails?.mainQuestionResponse
                      .responseResult as keyof typeof QuestionResultType
                  ]
                }
              </StatusIndicator>
            )}
            <TextContent data-testid="question-text-content">
              <small>
                Question ID: <strong>{item.questionDetails.questionId}</strong>
              </small>

              {questionDetails.questionDetails.questionText && (
                <SafeHtml
                  html={questionDetails.questionDetails.questionText.replace(
                    /<img[^>]*>/gi,
                    '',
                  )}
                />
              )}

              {item.responseDetails.mainQuestionResponse.responseComment && (
                <>
                  <p>
                    <small>Audit Comment :</small>
                    {item.responseDetails.mainQuestionResponse.responseComment}
                  </p>
                </>
              )}
            </TextContent>
          </SpaceBetween>

          {questionDetails?.subQuestions && questionDetails.subQuestions.length > 0 && (
            <Table
              data-testid="sub-questions-table"
              columnDefinitions={columnDefinitions}
              items={questionDetails.subQuestions.map(
                (sub) =>
                  ({
                    ...sub,
                    subQuestion: sub,
                    response: item.responseDetails.subQuestionResponses?.find(
                      (r) => r.subQuestionId === sub.subQuestionId,
                    )?.response,
                    parentQuestionId: item.questionDetails.questionId,
                  }) as SubQuestionResponseEntry,
              )}
              loading={isLoading}
              variant="borderless"
              empty="No sub-questions found"
            />
          )}
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <>
      <Container
        data-testid="question-summary-container"
        header={
          <Header
            data-testid="question-summary-header"
            variant="h3"
            description="Question and Audit Details are available when you generate the task"
            actions={
              <SpaceBetween direction="horizontal" size="m" alignItems="center">
                {paginationControls}
                {showLanguageDropdown && (
                  <LanguageDropdown
                    data-testid="language-dropdown"
                    selectedLanguage={getUserSelectedLanguage()}
                    defaultLanguage={DEFAULT_LANGUAGE}
                    filterLanguges={getPreferLanguageList()}
                    onLanguageChange={(value) => {
                      selectUserLanguage(value);
                    }}
                  />
                )}
              </SpaceBetween>
            }
          >
            Questions
          </Header>
        }
      >
        {sortedQuestions.length <= 0 && (
          <Box
            data-testid="no-questions-box"
            margin={{ vertical: 'xs' }}
            textAlign="center"
            color="inherit"
          >
            <SpaceBetween data-testid="no-questions-space" size="m">
              <StatusIndicator data-testid="no-questions-indicator" type="warning">
                No Questions.
              </StatusIndicator>
            </SpaceBetween>
          </Box>
        )}
        {translationError && (
          <Alert data-testid="translation-error-alert" type="error">
            Error : {translationError}
          </Alert>
        )}
        <SpaceBetween data-testid="questions-list" size="m" direction="vertical">
          {sortedQuestions?.map((item) => renderQuestionRow(item))}
        </SpaceBetween>
      </Container>

      <AuditSummaryTaskPanel
        visible={isTaskPanelOpen}
        onDismiss={() => {
          setIsTaskPanelOpen(false);
          setSelectedQuestion(undefined);
          setSelectedSubQuestionId(undefined);
          setSelectedExistingTask(undefined);
          setIsSelectedTaskReadOnly(false);
        }}
        audit={audit}
        question={selectedQuestion!}
        subQuestionId={selectedSubQuestionId}
        existingTask={selectedExistingTask}
        readOnly={isSelectedTaskReadOnly}
        onTaskCreated={() => {
          setIsTaskPanelOpen(false);
          setSelectedQuestion(undefined);
          setSelectedSubQuestionId(undefined);
          setSelectedExistingTask(undefined);
          setIsSelectedTaskReadOnly(false);
          setIsRefreshingTasks(true);
          onTaskCreated?.();
        }}
      />
    </>
  );
};

export default QuestionSummaryTable;
